/**
 * Dynamic form fields driven by tenant attribute definitions.
 *
 * Every entity with an `attributes` JSON column has a schema (system +
 * tenant-defined). This component renders the right input per `data_type`,
 * groups fields, and reports values as a plain object ready for the API.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";

/** One attribute definition as served by the settings API / asset type schema. */
export type AttributeDefinition = Schemas["AttributeDefinitionOut"];

/** Loose shape used by `AssetTypeOut.attribute_schema` (same keys, untyped). */
export type AttributeSchemaEntry = Pick<
  AttributeDefinition,
  | "key"
  | "label"
  | "label_i18n"
  | "description"
  | "data_type"
  | "required"
  | "default"
  | "options"
  | "unit"
  | "validation"
  | "group"
  | "sequence"
>;

/** Attribute values as sent/received by the API. */
export type AttributeValues = Record<string, unknown>;

/** Fetch active definitions for an entity (and optional scope such as an asset type key). */
export function useAttributeDefinitions(
  entityType: string,
  scope?: string,
  enabled = true,
): UseQueryResult<AttributeDefinition[]> {
  return useQuery({
    queryKey: ["attribute-definitions", entityType, scope ?? ""],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/settings/attribute-definitions", {
          params: { query: { entity_type: entityType, scope: scope ?? "" } },
        }),
      ),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Pick the localised label for a definition. */
export function attributeLabel(def: AttributeSchemaEntry, language: string): string {
  const i18n = def.label_i18n as Record<string, string> | undefined;
  return i18n?.[language] ?? def.label;
}

/** Build initial values with defaults applied for definitions missing from `current`. */
export function withDefaults(
  defs: AttributeSchemaEntry[],
  current: AttributeValues | undefined,
): AttributeValues {
  const out: AttributeValues = { ...(current ?? {}) };
  for (const d of defs) {
    if (out[d.key] === undefined && d.default !== null && d.default !== undefined) {
      out[d.key] = d.default;
    }
  }
  return out;
}

/** Render any attribute value as an input string. */
export function asText(raw: unknown): string {
  if (raw === null || raw === undefined) {
    return "";
  }
  if (typeof raw === "string") {
    return raw;
  }
  if (typeof raw === "number" || typeof raw === "boolean") {
    return String(raw);
  }
  return JSON.stringify(raw);
}

/** Coerce raw input strings to the API type for a definition. */
export function coerceAttribute(def: AttributeSchemaEntry, raw: unknown): unknown {
  if (raw === "" || raw === null || raw === undefined) {
    return null;
  }
  switch (def.data_type) {
    case "integer": {
      const n = Number.parseInt(asText(raw), 10);
      return Number.isNaN(n) ? null : n;
    }
    case "number":
    case "money": {
      const n = Number(asText(raw));
      return Number.isNaN(n) ? null : n;
    }
    case "boolean":
      return raw === true || raw === "true";
    case "multi_choice":
      return Array.isArray(raw) ? raw : [asText(raw)];
    default:
      return raw;
  }
}

/** Drop null/empty entries so the API only receives provided keys. */
export function compactAttributes(
  defs: AttributeSchemaEntry[],
  values: AttributeValues,
): AttributeValues {
  const out: AttributeValues = {};
  for (const d of defs) {
    const v = coerceAttribute(d, values[d.key]);
    if (v !== null && !(Array.isArray(v) && v.length === 0)) {
      out[d.key] = v;
    }
  }
  return out;
}

/** Props for `AttributeFields`. */
export interface AttributeFieldsProps {
  definitions: AttributeSchemaEntry[];
  values: AttributeValues;
  onChange: (values: AttributeValues) => void;
  errors?: Record<string, string>;
  idPrefix?: string;
  disabled?: boolean;
}

/** Render inputs for every definition, grouped by `group`. */
export function AttributeFields({
  definitions,
  values,
  onChange,
  errors = {},
  idPrefix = "attr",
  disabled,
}: AttributeFieldsProps): ReactElement | null {
  const { t, i18n } = useTranslation();
  if (definitions.length === 0) {
    return null;
  }
  const sorted = [...definitions].sort(
    (a, b) => a.group.localeCompare(b.group) || a.sequence - b.sequence,
  );
  const groups = new Map<string, AttributeSchemaEntry[]>();
  for (const d of sorted) {
    const list = groups.get(d.group) ?? [];
    list.push(d);
    groups.set(d.group, list);
  }
  const update = (key: string, value: unknown): void => onChange({ ...values, [key]: value });

  const renderInput = (d: AttributeSchemaEntry): ReactElement => {
    const id = `${idPrefix}-${d.key}`;
    const raw = values[d.key];
    const options = (d.options ?? []) as { value: string; label: string }[];
    switch (d.data_type) {
      case "text":
        return (
          <Textarea
            id={id}
            value={asText(raw)}
            onChange={(e) => update(d.key, e.target.value)}
            disabled={disabled}
          />
        );
      case "boolean":
        return (
          <Select
            id={id}
            value={asText(raw)}
            onChange={(e) =>
              update(d.key, e.target.value === "" ? null : e.target.value === "true")
            }
            disabled={disabled}
          >
            <option value="">—</option>
            <option value="true">{t("app.yes")}</option>
            <option value="false">{t("app.no")}</option>
          </Select>
        );
      case "choice":
        return (
          <Select
            id={id}
            value={asText(raw)}
            onChange={(e) => update(d.key, e.target.value || null)}
            disabled={disabled}
          >
            <option value="">—</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        );
      case "multi_choice": {
        const selected = Array.isArray(raw) ? (raw as string[]) : [];
        return (
          <div className="flex flex-wrap gap-3" role="group" aria-labelledby={`${id}-label`}>
            {options.map((o) => (
              <Checkbox
                key={o.value}
                id={`${id}-${o.value}`}
                label={o.label}
                checked={selected.includes(o.value)}
                disabled={disabled}
                onChange={(e) =>
                  update(
                    d.key,
                    e.target.checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value),
                  )
                }
              />
            ))}
          </div>
        );
      }
      case "integer":
      case "number":
      case "money": {
        const v = (d.validation ?? {}) as { min?: number; max?: number };
        return (
          <Input
            id={id}
            type="number"
            step={d.data_type === "integer" ? 1 : "any"}
            min={v.min}
            max={v.max}
            value={asText(raw)}
            onChange={(e) => update(d.key, e.target.value)}
            disabled={disabled}
          />
        );
      }
      case "date":
        return (
          <Input
            id={id}
            type="date"
            value={asText(raw).slice(0, 10)}
            onChange={(e) => update(d.key, e.target.value || null)}
            disabled={disabled}
          />
        );
      case "datetime":
        return (
          <Input
            id={id}
            type="datetime-local"
            value={asText(raw).slice(0, 16)}
            onChange={(e) =>
              update(d.key, e.target.value ? new Date(e.target.value).toISOString() : null)
            }
            disabled={disabled}
          />
        );
      case "url":
        return (
          <Input
            id={id}
            type="url"
            value={asText(raw)}
            onChange={(e) => update(d.key, e.target.value)}
            disabled={disabled}
          />
        );
      default:
        return (
          <Input
            id={id}
            value={asText(raw)}
            onChange={(e) => update(d.key, e.target.value)}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      {[...groups.entries()].map(([group, defs]) => (
        <fieldset key={group || "_"} className="space-y-3">
          {group ? (
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group}
            </legend>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {defs.map((d) => {
              const label = `${attributeLabel(d, i18n.language)}${d.unit ? ` (${d.unit})` : ""}${d.required ? " *" : ""}`;
              return (
                <Field
                  key={d.key}
                  id={`${idPrefix}-${d.key}`}
                  label={label}
                  hint={d.description || undefined}
                  error={errors[d.key]}
                >
                  {renderInput(d)}
                </Field>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
